import isEmpty from 'lodash/isEmpty';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import * as LoginActions from 'features/login/store/login.action';
import { API_CONFIG, colorPallet, ENVIRONMENT } from 'shared/constants/constants';
import { useWhitelabelConfigStatus } from 'shared/hooks/useWhitelabelConfigStatus';
import { LockIcon, UnlockIcon } from 'shared/icons/icon';
import { IAllGlobalSearchData, State } from 'shared/interface';
import { ScaleType } from 'shared/interface/enums';
import httpService from 'shared/services/http.service';
import localizationConstants from 'shared/util/translation/constants';
import { Translate } from 'shared/util/translation/translate';
import { capitalizeFirstLetter, createAction, createLoadingSelector, debounce, replaceHashtagFromUrl } from 'shared/util/utility';
import * as actionTypes from 'store/action-types';

import Confirm from '../confirm/confirm';
import { Loader } from '../spinner/spinner';
import EmptySearchComponent from './emptySearchComponent';
import HeaderButtons from './headerButtons';
import SearchComponent from './searchComponent';
import { v4 as uuid } from 'uuid';
let cancel_req: any;
type GlobalSearchTarget = 'Price' | 'News';
interface UIState {
	isOpen: boolean;
	isPopUpOpen: boolean;
	allGlobalSearchData: IAllGlobalSearchData;
	activeSearchType: GlobalSearchTarget;
	isActive: boolean;
	loading: boolean;
}

const TopHeader: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const myRef = useRef<any>(null);
	const { whitelabelId, news_status, material_status } = useWhitelabelConfigStatus();
	const [state, setState] = useState<UIState>({
		isOpen: false,
		isPopUpOpen: false,
		allGlobalSearchData: {} as IAllGlobalSearchData,
		isActive: false,
		activeSearchType: material_status === 'hideall' ? 'News' : 'Price',
		loading: false
	});
	const [materialSearchTitle, setMaterialSearchTitle] = useState('');
	const [newsSearchTitle, setNewsSearchTitle] = useState('');
	const loadingSelector = createLoadingSelector(['AUTH_LOGOUT']);

	const authData = useSelector((state: State) => state.auth.authData);
	const userData = useSelector((state: State) => state.auth.userData, shallowEqual);
	const language = useSelector((state: State) => state.siteConfig.language);
	const chartView = useSelector((state: State) => state.siteConfig.chartView);
	const logoutLoading = useSelector((state: State) => loadingSelector(state));
	const dispatch = useDispatch();

	const searchData = useCallback(
		debounce((searchWord: string, searchType: GlobalSearchTarget) => {
			setState((prevState) => ({
				...prevState,
				loading: true,
				allGlobalSearchData: {} as IAllGlobalSearchData
			}));
			if (searchWord !== '') {
				const param = {
					word: searchWord,
					...(whitelabelId && { whitelabel_id: whitelabelId })
				};
				cancel_req && cancel_req();
				httpService
					.get(searchType === 'Price' ? API_CONFIG.path.materialSearch : API_CONFIG.path.newsSearch, param, { executor: (c) => (cancel_req = c) })
					.then((response) => {
						if (!response.reqCanceled) {
							setState((prevState) => ({
								...prevState,
								allGlobalSearchData:
									searchType === 'Price'
										? ({ material: response.material || [], news: [] } as IAllGlobalSearchData)
										: ({ material: [], news: response.news || [] } as IAllGlobalSearchData),
								loading: false
							}));
						}
					})
					.catch((error) => console.error(error));
			} else {
				setState((prevState) => ({
					...prevState,
					loading: false,
					allGlobalSearchData: {} as IAllGlobalSearchData
				}));
			}
		}, 300),
		[whitelabelId]
	);

	const handleClickOutside = useCallback(
		(event: any) => {
			if (myRef.current && !myRef.current.contains(event.target) && state.isActive) {
				setMaterialSearchTitle('');
				setNewsSearchTitle('');
				setState((prevState) => ({
					...prevState,
					isActive: false
				}));
			}
		},
		[state.isActive]
	);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			// Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);

	useEffect(() => {
		if (location.pathname) {
			setMaterialSearchTitle('');
			setNewsSearchTitle('');
		}
	}, [location.pathname]);

	const toggleModalStatus = () => {
		setState((prevState) => ({
			...prevState,
			isOpen: !prevState.isOpen
		}));
	};

	const closeGlobalSearch = () => {
		setMaterialSearchTitle('');
		setNewsSearchTitle('');
		setState((prevState) => ({
			...prevState,
			isPopUpOpen: false,
			isActive: false
		}));
	};

	const renderMaterialMeta = (_materialData: any) => {
		// No meta badge in the price quick-search results: neither the source ("Quelle") nor the
		// country (e.g. "CN") should be shown here.
		return null;
	};

	const renderSearchComponents = (type: GlobalSearchTarget) => {
		const { allGlobalSearchData, loading } = state;
		switch (type) {
			case 'News':
				return (
					<>
						{isEmpty(allGlobalSearchData.news) && loading && <Loader />}
						{!isEmpty(allGlobalSearchData.news) && (
							<>
								{allGlobalSearchData.news.map((newsData, index: number) => (
									<div key={index} className='d-flex justify-content-space-between align-items-center pt--10 pb-10  '>
										<p
											className='font--14px global-search-title cursor-pointer'
											onClick={() => {
												closeGlobalSearch();
												navigate(
													`/news/fullNewsArticle/0?newsId=${newsData.id}${
														whitelabelId && news_status !== 'inactive' ? `&whitelabelId=${whitelabelId}` : ''
													}`
												);
											}}
										>
											{newsData.titel}
										</p>
										<p className='font--14px global-search-type'>{newsData.type}</p>
									</div>
								))}
								<p
									onClick={() => {
										closeGlobalSearch();
										navigate(
											`/news/articleList/0?word=${encodeURIComponent(newsSearchTitle)}${
												whitelabelId && news_status !== 'inactive' ? `&whitelabelId=${whitelabelId}` : ''
											}`
										);
									}}
									className='font--14px global-search-title cursor-pointer'
								>
									<Translate text={localizationConstants.more} />
								</p>
							</>
						)}
						{isEmpty(allGlobalSearchData.news) && !loading && <EmptySearchComponent />}
					</>
				);
			case 'Price':
				return (
					<>
						{isEmpty(allGlobalSearchData.material) && loading && <Loader />}
						{!isEmpty(allGlobalSearchData.material) && (
							<>
								{allGlobalSearchData.material.map((materialData, index: number) => {
									return (
										<div key={index} className='d-flex justify-content-space-between align-items-center pt--10 pb-10'>
											{isEmpty(userData.admin) && (
												<div className='icon__wrapper'>
													{materialData.disallowed === 1 ? (
														<LockIcon className='lock-icon' />
													) : (
														<UnlockIcon className='unlock-icon' />
													)}
												</div>
											)}{' '}
											<div>
												<p
													className='font--14px global-search-title cursor-pointer no-margin'
													onClick={() => {
														closeGlobalSearch();
														navigate(
															`/materials/futureTable?globalSearchWordListenId=${
																materialData.id
															}&boerse=${replaceHashtagFromUrl(materialData.boerse)}&basis_symbol=${
																materialData.basis_symbol
															}&symbol=${replaceHashtagFromUrl(materialData.symbol)}&currency=${materialData.waehrung}`,
															{
																state: {
																	chartSelection: {
																		span: '1y',
																		type:
																			chartView === ScaleType.PERCENTAGE_CHANGE ||
																			chartView === ScaleType.PERCENTAGE_CHANGE_TOTAL
																				? 'line'
																				: 'area',
																		customChartSpan: undefined
																	},
																	advanceChartConfig: {
																		chartSettings: [
																			{
																				symbol: materialData.symbol,
																				boerse: materialData.boerse,
																				basis_symbol: materialData.basis_symbol,
																				currency: {
																					label: materialData.waehrung,
																					value: materialData.waehrung
																				},
																				axis: {
																					label: 'Right',
																					value: 'right'
																				},
																				color: colorPallet[0],
																				disallowed: materialData.disallowed ?? 0,
																				history: {
																					label: t(localizationConstants.none),
																					value: ''
																				},
																				id: uuid(),
																				indicators: [],
																				name: '',
																				unit: {
																					label: materialData.einheit,
																					value: materialData.einheit
																				},
																				forecastedColor: []
																			}
																		]
																	}
																}
															}
														);
													}}
												>
													{language === 'en' && materialData.name_eng !== ''
														? materialData.name_eng
														: materialData.name_deu}
												</p>
												{renderMaterialMeta(materialData)}
											</div>
											<p className='font--14px global-search-type'>
												<Translate text={localizationConstants.price} />
											</p>
										</div>
									);
								})}
								<p
									onClick={() => {
										closeGlobalSearch();
										navigate(`/materials/search?wordSearch=${encodeURIComponent(materialSearchTitle)}`);
									}}
									className='font--14px global-search-title cursor-pointer'
								>
									<Translate text={localizationConstants.more} />
								</p>
							</>
						)}
						{isEmpty(allGlobalSearchData.material) && !loading && <EmptySearchComponent />}
					</>
				);
			default:
				break;
		}
	};

	const handleOnEnter = (key: string, searchType: GlobalSearchTarget) => {
		if (key === 'Enter') {
			if (searchType === 'Price') {
				closeGlobalSearch();
				navigate(`/materials/search?wordSearch=${encodeURIComponent(materialSearchTitle)}`);
			} else {
				closeGlobalSearch();
				navigate(
					`/news/articleList/0?word=${encodeURIComponent(newsSearchTitle)}${
						whitelabelId && news_status !== 'inactive' ? `&whitelabelId=${whitelabelId}` : ''
					}`
				);
			}
		}
	};

	const onLogout = () => {
		LoginActions.logout(dispatch);
		dispatch(
			createAction(actionTypes.UPDATE_SITE_CONFIG, {
				sessionExpireMessage: ''
			})
		);
	};
	if (!authData?.auth) {
		return <Navigate replace to='/' />;
	}

	const handleOnSearch = (event: ChangeEvent<HTMLInputElement>, searchType: GlobalSearchTarget) => {
		const value = event.target.value;
		if (searchType === 'Price') {
			setMaterialSearchTitle(value);
		} else {
			setNewsSearchTitle(value);
		}
		setState((prevState) => ({
			...prevState,
			isPopUpOpen: true,
			isActive: true,
			activeSearchType: searchType
		}));
		searchData(value, searchType);
	};

	const { activeSearchType, isActive, isPopUpOpen } = state;
	const activeSearchTitle = activeSearchType === 'Price' ? materialSearchTitle : newsSearchTitle;
	const showMaterialSearch = material_status !== 'hideall';
	const showNewsSearch = news_status !== 'hideall';
	const shouldShowSearchPopup = isPopUpOpen && activeSearchTitle !== '' && isActive;

	return (
		<>
			<div className='border-bottom full-width top-header' ref={myRef}>
				<div className='navbar navbar-static-top d-flex align-items-center justify-content-space-between '>
					<div className='navbar-header position--relative d-flex justify-content-center align-items-center'>
						{(showMaterialSearch || showNewsSearch) && (
							<div className='quick-search-fields d-flex align-items-center'>
								{showMaterialSearch && (
									<div className='quick-search-field position--relative'>
										<SearchComponent
											searchTitle={materialSearchTitle}
											handleOnEnter={(key) => handleOnEnter(key, 'Price')}
											handleOnSearch={(event) => handleOnSearch(event, 'Price')}
											placeholder={t(localizationConstants.searchMaterialEtc)}
										/>
										{shouldShowSearchPopup && activeSearchType === 'Price' && (
											<div className='position--absolute global-search-box separated-search-box'>
												<div className='global-search-content separated-search-content'>{renderSearchComponents('Price')}</div>
											</div>
										)}
									</div>
								)}
								{showNewsSearch && (
									<div className='quick-search-field position--relative'>
										<SearchComponent
											searchTitle={newsSearchTitle}
											handleOnEnter={(key) => handleOnEnter(key, 'News')}
											handleOnSearch={(event) => handleOnSearch(event, 'News')}
											placeholder={t(localizationConstants.searchNewsEtc)}
										/>
										{shouldShowSearchPopup && activeSearchType === 'News' && (
											<div className='position--absolute global-search-box separated-search-box'>
												<div className='global-search-content separated-search-content'>{renderSearchComponents('News')}</div>
											</div>
										)}
									</div>
								)}
							</div>
						)}
						{ENVIRONMENT !== 'production' && (
							<p className='no-margin font--16px line-height--20 font-weight--600 ml-15'>{capitalizeFirstLetter(ENVIRONMENT)}</p>
						)}
					</div>
					<div className='d-flex justify-content-center align-items-center '>
						<p className='no-margin font--14px line-height--25 font-weight--600 mr-6'>
							{userData.vorname} {userData.nachname}
						</p>
						<div className='d-flex'>
							<HeaderButtons toggleModalStatus={toggleModalStatus} />
						</div>
					</div>
				</div>
				{state.isOpen && (
					<Confirm
						show={state.isOpen}
						handleClose={toggleModalStatus}
						handleConfirm={onLogout}
						loading={logoutLoading}
						message={`${t(localizationConstants.confirmLogout)}`}
					/>
				)}
			</div>
		</>
	);
};

export default TopHeader;
